var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var LoggedInUser_1;
/** @file This file defines the users used in the vue app. */
import axios from 'axios';
import { Left, Maybe, NonEmptyList, Right } from 'purify-ts';
import * as codec from 'purify-ts/Codec';
import * as apiModels from '@codegrade/api';
import { array, datetime, expr, immutable, itertools, map, parsers, sort } from '@codegrade/utils';
import * as models from '@/models';
import { GlobalPermissions as GPerm } from '@/permissions';
import { CoursesStore, SiteSettingsStore, store, TenantsStore, UserStore } from '@/store';
import * as utils from '@/utils';
/** The base of a user in CodeGrade. */
export class User {
    /** The id of the user. */
    id;
    /** The name of the user, might be `Nothing` if we didn't receive the PII. */
    name;
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    username;
    /* eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation. */
    is_test_student;
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    tenantId;
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    constructor(data) {
        this.id = data.id;
        this.name = data.name;
        this.username = data.username;
        this.is_test_student = data.isTestStudent;
        this.tenantId = data.tenantId;
    }
    /**
     * Get the anonymization method to use, if any.
     *
     * @param assignment The assignment for which you might need to anonymize.
     * @returns Maybe a method to get the anonymized name of the user with.
     */
    getAnonymizationMethod(assignment) {
        // Never anonymize the test student, early fast bailout.
        if (assignment == null || this.isTestStudent) {
            return undefined;
        }
        const method = assignment.anonymizationMethod.extract();
        if (method == null) {
            return undefined;
        }
        // Never anonymize the logged in user
        const loggedInUser = UserStore.loggedInUser();
        if (loggedInUser.mapOrDefault(u => this.isEqualOrMemberOf(u.user), false)) {
            return undefined;
        }
        // Do not anonymize members of the group of the logged in user.
        const isGroupMember = utils.maybeUtils
            .and(loggedInUser, Maybe.fromNullable(assignment.group_set?.id))
            .chainNullable(([user, groupSetId]) => store.getters['users/getGroupInGroupSetOfUser'](groupSetId, user.user.id))
            .map(group => this.isMemberOf(group.group));
        if (isGroupMember.orDefault(false)) {
            return undefined;
        }
        return method;
    }
    /**
     * Should we anonymize for the given assignment.
     *
     * @param assignment The assignment to check.
     * @returns If the name of this user should be anonymized.
     */
    shouldAnonymize(assignment) {
        return this.getAnonymizationMethod(assignment) != null;
    }
    /**
     * Potentially anonymize the given name for the given assignment.
     *
     * @param assignment The assignment to potentially anonymize for.
     * @param name The name to potentially anonymize for.
     * @returns The given name or an anonymized name, depending on the assignment settings.
     */
    anonymizeName(assignment, name) {
        const method = this.getAnonymizationMethod(assignment);
        if (method == null) {
            return name;
        }
        return method(this.id);
    }
    /**
     * Get the name we should display for this user.
     *
     * WARNING: If you change this method, also change the type definition in
     * `ts/packages/documents/src/plagiarism.ts::User`.
     *
     * @param assignment The assignment to anonymize for.
     * @param prefixGroup Should we prefix group names with "Group".
     * @returns The name of the user as it should be displayed, or `Nothing` if the name is removed
     * from our system.
     */
    getDisplayName(assignment, prefixGroup = true) {
        if (this.group != null) {
            const groupName = this.anonymizeName(assignment, this.group.name);
            if (prefixGroup) {
                return Maybe.of(`Group "${groupName}"`);
            }
            else {
                return Maybe.of(groupName);
            }
        }
        else {
            const method = this.getAnonymizationMethod(assignment);
            if (method == null) {
                return this.name;
            }
            return Maybe.of(method(this.id));
        }
    }
    /** @returns The set of ids of all contained users. */
    getContainedUserIds() {
        return new Set(this.getContainedUsers().map(user => user.id));
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    get isTestStudent() {
        return !!this.is_test_student;
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    isEqualOrMemberOf(other) {
        if (other == null) {
            return false;
        }
        else if (other.id === this.id) {
            return true;
        }
        else if (other.isGroup) {
            return this.isMemberOf(other.group);
        }
        else if (this.isGroup) {
            return other.isMemberOf(this.group);
        }
        return false;
    }
    /* eslint-enable lines-between-class-members */
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    isMemberOf(group) {
        if (group == null) {
            return false;
        }
        return group.memberIds.some(memberId => memberId === this.id);
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    static findUserById(userId) {
        if (userId == null) {
            return null;
        }
        return store.getters['users/getUser'](userId);
    }
    /** Get the tenant for this user, ensuring that it is loaded if need be. */
    get tenant() {
        return this.tenantId.chain(tId => TenantsStore.getTenant()(tId).ifNothing(() => TenantsStore.loadSingleTenant({ tenantId: tId })));
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    _baseEquals(other) {
        return (this.id === other.id &&
            this.name === other.name &&
            this.username === other.username &&
            this.tenantId.equals(other.tenantId));
    }
}
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
export class Group {
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    id;
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    memberIds;
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    name;
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    groupSetId;
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    createdAt;
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    constructor(data) {
        // Make sure id can never be overridden, as this would break all code.
        this.id = data.id;
        data.members.forEach(user => {
            store.dispatch('users/addOrUpdateUser', { user });
        });
        this.memberIds = data.members.map(m => m.id);
        this.name = data.name;
        this.groupSetId = data.groupSetId;
        this.createdAt = data.createdAt;
    }
    /* eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation. */
    get created_at() {
        return datetime.formatDate(this.createdAt);
    }
    /* eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation. */
    get group_set_id() {
        return this.groupSetId;
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    get members() {
        return this.memberIds.map(User.findUserById).filter((x) => x != null);
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    getMemberStates(assignmentId) {
        return axios.get(`/api/v1/assignments/${assignmentId}/groups/${this.id}/member_states/`);
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    equals(other) {
        return (this.id === other.id &&
            this.name === other.name &&
            this.groupSetId === other.groupSetId &&
            this.createdAt.isSame(other.createdAt) &&
            this.memberIds.length === other.memberIds.length &&
            utils.setEq(new Set(this.memberIds), new Set(other.memberIds)));
    }
}
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
export class NormalUser extends User {
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    group;
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    constructor(data) {
        super(data);
        this.group = null;
    }
    /** @inheritdoc */
    getGroupMembers() {
        return [];
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    static getCurrentUser() {
        const myId = UserStore.id();
        const res = User.findUserById(myId);
        if (res == null || res.isGroup) {
            // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
            throw new Error(`Could not find currently logged in user, found: ${res}`);
        }
        return res;
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    isGroup = false;
    /** @inheritdoc */
    getContainedUsers() {
        return [this];
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    equals(other) {
        if (!(other instanceof NormalUser)) {
            return false;
        }
        return this._baseEquals(other);
    }
}
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
class _FrozenNormalUser extends NormalUser {
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    constructor(data) {
        super(data);
        Object.freeze(this);
    }
}
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
export class GroupUser extends User {
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    group;
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    constructor(data, group) {
        super(data);
        this.group = Object.freeze(new Group(group));
    }
    /** @inheritdoc */
    getGroupMembers() {
        return this.group.members;
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    isGroup = true;
    /** @inheritdoc */
    getContainedUsers() {
        return this.group.members;
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    equals(other) {
        if (other instanceof NormalUser) {
            return false;
        }
        return this._baseEquals(other) && this.group.equals(other.group);
    }
}
/** A parser for a session. */
export const loggedInUserSessionParser = codec.Codec.interface({
    token: codec.string,
    restrictions: apiModels.SessionRestrictionData,
    expiresAt: codec.maybe(parsers.moment),
});
/** Parser for the method the user used to log into the app. */
export const loginMethodParser = parsers.TaggedUnionBuilder.create('tag')
    .add('username-password', codec.Codec.interface({
    tenantId: codec.maybe(codec.string),
}))
    .add('lti', codec.Codec.interface({
    lms: codec.maybe(codec.string),
}))
    .add('sso', codec.Codec.interface({
    tenantId: codec.maybe(codec.string),
}))
    .add('assignment-login', codec.Codec.interface({
    assignmentId: codec.number,
    loginUuid: codec.string,
}))
    .build();
/** The user that is currently logged in. */
let LoggedInUser = LoggedInUser_1 = class LoggedInUser extends NormalUser {
    /** The session of the user. */
    #sessions;
    /** All sessions of the user. */
    get sessions() {
        return this.#sessions;
    }
    /** The method this user used to log in. */
    loginMethod;
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    permissions;
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    canSeeHidden;
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    email;
    /** The payments done by this user. */
    #payments;
    /** The coupons used by the user. */
    #usedCoupons;
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    constructor(data, extraData) {
        super(data);
        this.#sessions = extraData.sessions;
        this.loginMethod = extraData.loginMethod;
        this.permissions = extraData.permissions;
        this.canSeeHidden = extraData.canSeeHidden;
        this.email = extraData.email;
        this.#payments = new map.ReadonlyMaybeMap(utils
            .groupByIter(sort.sortBy(extraData.payments.map(p => new models.Transaction(p)), payment => [payment.successAt, payment.updatedAt], 
        // We want the newest for every state in our
        // map, so they should be add the end of the
        // array.
        { reverse: false }), payment => payment.coursePriceId, utils.compare.compare)
            .map(([priceId, transactions]) => [
            priceId,
            new map.ReadonlyMaybeMap(transactions.map(t => [t.state, t])),
        ]));
        this.#usedCoupons = new map.ReadonlyMaybeMap(extraData.usedCoupons.map(couponUsage => expr.match(couponUsage, 'scope', {
            course: cu => [
                cu.coupon.coursePrice.course.id,
                new apiModels.CourseCouponUsage(cu),
            ],
            tenant: cu => [cu.course.id, new apiModels.TenantCouponUsage(cu)],
        })));
    }
    /** @returns The current active session of the user. */
    get session() {
        return array.last(this.sessions);
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    setPermission(perm, value) {
        const newPerms = Object.freeze({ ...this.permissions, [perm]: value });
        return new LoggedInUser_1(this, {
            sessions: this.sessions,
            loginMethod: this.loginMethod,
            permissions: newPerms,
            canSeeHidden: this.canSeeHidden,
            email: this.email,
            payments: this._getAllTransactions(),
            usedCoupons: this._getAllCoupons(),
        });
    }
    /**
     * Get all the transactions for the user of a given price.
     *
     * @param price The price like to check payments for.
     * @returns The transactions for the given price as map.
     */
    getPaymentsFor(price) {
        return this.#payments.get(price.id).orDefaultLazy(() => new map.ReadonlyMaybeMap());
    }
    /**
     * Get the transaction for the user of a given course price.
     *
     * @param price The course price to get the transaction for.
     * @returns The transaction if we have any.
     */
    getAnyPaymentFor(price) {
        const payments = this.getPaymentsFor(price);
        return payments.get('success', 'pending', 'failed', 'refunded', 'refunding');
    }
    /**
     * Retrieves the coupon, if any, used to pay for the course, by its price object.
     *
     * @param price The Price connected to the course.
     * @returns The coupon, if any, used to pay for the course.
     */
    getCouponsFor(price) {
        return this.#usedCoupons.get(price.course.id);
    }
    /** @returns All transactions of the user. */
    _getAllTransactions() {
        return itertools.flatMap1(this.#payments.values(), payment => payment.values());
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    _getAllCoupons() {
        return Array.from(this.#usedCoupons.values());
    }
    // Kept for legacy purposes.
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    get user() {
        return this;
    }
    /**
     * Removes the most recent session from the user's session list and returns the updated user
     * instance.
     *
     * If removing the session would leave the user without any sessions, an error is returned.
     *
     * @returns Either an error if trying to remove the last session or an object containing the old
     * session and a new user instance.
     */
    popSession() {
        const newSessions = this.sessions.slice();
        const oldSession = newSessions.pop();
        if (!NonEmptyList.isNonEmpty(newSessions) || oldSession == null) {
            return Left(new Error('Cannot remove last active session'));
        }
        const newUser = new LoggedInUser_1(this, {
            canSeeHidden: this.canSeeHidden,
            email: this.email,
            sessions: newSessions,
            loginMethod: this.loginMethod,
            payments: this._getAllTransactions(),
            permissions: this.permissions,
            usedCoupons: this._getAllCoupons(),
        });
        return Right({ oldSession, newUser });
    }
    /**
     * Adds a new session to the user's current list of sessions, creating a new user instance with
     * the updated session list.
     *
     * The session list is managed as a non-empty list, ensuring the user always has at least one
     * active session.
     *
     * @param session The session to be added to the user's current sessions.
     * @returns A new instance of LoggedInUser with the updated session list.
     */
    addSession(session) {
        return new LoggedInUser_1(this, {
            canSeeHidden: this.canSeeHidden,
            email: this.email,
            sessions: NonEmptyList(this.sessions).concat([session]),
            loginMethod: this.loginMethod,
            payments: this._getAllTransactions(),
            permissions: this.permissions,
            usedCoupons: this._getAllCoupons(),
        });
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    updateFromApi(data) {
        let newTransactions = this._getAllTransactions();
        // We get an empty array if we are another user.
        if (data.payments.length > 0) {
            newTransactions = data.payments.map(p => new models.Transaction(p));
        }
        return new LoggedInUser_1(data, {
            canSeeHidden: this.canSeeHidden,
            email: data.email,
            sessions: this.sessions,
            loginMethod: this.loginMethod,
            payments: newTransactions,
            permissions: data.permissions ?? this.permissions,
            usedCoupons: data.usedCoupons,
        });
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    putTransactions(transactions) {
        const allTransactions = this._getAllTransactions();
        transactions.forEach(transaction => {
            array
                .findMaybeIndex(t => t.id === transaction.id, allTransactions)
                .caseOf({
                Just: index => {
                    allTransactions[index] = transaction;
                },
                Nothing: () => {
                    allTransactions.push(transaction);
                },
            });
        });
        return new LoggedInUser_1(this, {
            canSeeHidden: this.canSeeHidden,
            email: this.email,
            sessions: this.sessions,
            loginMethod: this.loginMethod,
            payments: allTransactions,
            permissions: this.permissions,
            usedCoupons: this._getAllCoupons(),
        });
    }
    /**
     * Creates a new LoggedInUser object with the updated list of updated coupons.
     *
     * @param usedCoupons The array of coupons used by the user, to be put in the store.
     * @returns A new LoggedIn user object with the updated list of used coupons.
     */
    putUsedCoupons(usedCoupons) {
        const allUsedCoupons = this._getAllCoupons();
        usedCoupons.forEach(usedCoupon => {
            array
                .findMaybeIndex(cu => cu.coupon.id === usedCoupon.coupon.id &&
                cu.userId === usedCoupon.userId &&
                cu.id === usedCoupon.id, allUsedCoupons)
                .caseOf({
                Just: index => {
                    allUsedCoupons[index] = usedCoupon;
                },
                Nothing: () => {
                    allUsedCoupons.push(usedCoupon);
                },
            });
        });
        return new LoggedInUser_1(this, {
            canSeeHidden: this.canSeeHidden,
            email: this.email,
            sessions: this.sessions,
            loginMethod: this.loginMethod,
            payments: this._getAllTransactions(),
            permissions: this.permissions,
            usedCoupons: allUsedCoupons,
        });
    }
    /**
     * Check whether the user normally would have to pay for the course.
     *
     * This does not yet check if they have paid already!
     *
     * @param tenantId The tenant id if we have any.
     * @returns Whether we should pay for the course or not in the general sense.
     */
    shouldPayForCourses(tenantId) {
        return (!UserStore.hasTenantPermission()(GPerm.can_skip_payment, tenantId) &&
            SiteSettingsStore.getSetting()('STUDENT_PAYMENT_ENABLED', tenantId));
    }
    /**
     * Get whether the user should pay for a course in the general sense.
     *
     * This does not yet check if they have paid already!
     *
     * @param course The course to check has to be paid.
     * @returns Whether the user should pay for the course in the general sense.
     */
    shouldPayFor(course) {
        return course.price.filter(p => this.shouldPayForCourses(p.course.tenantId));
    }
    /**
     * Check whether the user has already paid for this course using a coupong.
     *
     * @param price The course price to check coupon usage for.
     * @returns Whether the user has already paid with coupon.
     */
    hasPaidWithCoupon(price) {
        return this.getCouponsFor(price).isJust();
    }
    /**
     * Check whether the user has the permission to skip payment for a course.
     *
     * @param course The course to check the payment status for.
     * @returns Whether the user has permission to skip the payment.
     */
    canSkipPaymentFor(course) {
        return UserStore.hasTenantPermission()(GPerm.can_skip_payment, course.tenantId);
    }
    /**
     * Check whether the user has already paid for the course price.
     *
     * @param price The course price to check payment for.
     * @returns Whether the user has already payed for this course price.
     */
    hasPaidFor(price) {
        if (this.shouldPayForCourses(price.course.tenantId)) {
            return (this.getPaymentsFor(price).get('success').isJust() || this.hasPaidWithCoupon(price));
        }
        return true;
    }
    /**
     * Check if the user has the specified permission in the course.
     *
     * @param this The logged in user.
     * @param permission The permission to check for the user in the course.
     * @param course The course to check for the permission.
     * @returns Whether the user has the permission in the course.
     */
    hasCoursePermission(permission, course) {
        const removedPermissions = this.session.restrictions.removedPermissions;
        if (removedPermissions != null && removedPermissions.course.has(permission.value)) {
            return false;
        }
        return CoursesStore.getPermissionsForCourse()(course.id).caseOf({
            Just: perms => {
                let res = perms[permission.value] || UserStore.isAdminIn()(course.tenantId);
                if (permission.needsPayment) {
                    res &&= course.price.mapOrDefault(p => this.hasPaidFor(p), true);
                }
                return res;
            },
            Nothing: () => UserStore.isAdminIn()(course.tenantId),
        });
    }
    /**
     * A getter to retrieve the current state of the student view.
     *
     * If the user has no removed course permissions, the state is 'disabled'.
     * Otherwise, the state is 'enabled' and indicates whether the user can leave the session.
     * We can only leave if we have a base session token to fall back to.
     *
     * @returns An object representing the state of the student view.
     */
    getStudentViewState() {
        const removedPerms = this.session.restrictions.removedPermissions?.course ?? new Set();
        if (removedPerms.size === 0) {
            return Object.freeze({ tag: 'disabled' });
        }
        return Object.freeze({
            tag: 'enabled',
            canLeave: this.sessions.length > 1,
        });
    }
};
LoggedInUser = LoggedInUser_1 = __decorate([
    immutable.immutable
], LoggedInUser);
export { LoggedInUser };
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
export function makeUser(data, oldObject = null) {
    if (data instanceof NormalUser || data instanceof GroupUser) {
        return data;
    }
    else if (data instanceof apiModels.User) {
        return data.group.caseOf({
            Just: group => new GroupUser(data, group),
            Nothing: () => new _FrozenNormalUser(data),
        });
    }
    else if (data instanceof apiModels.UserWithoutGroup) {
        return new _FrozenNormalUser(data);
    }
    const maybeOld = Maybe.fromNullable(oldObject);
    const newData = {
        id: data.id,
        name: Maybe.fromNullable(data.name).altLazy(() => maybeOld.chain(old => old.name)),
        username: data.username ?? maybeOld.map(old => old.username).extract(),
        isTestStudent: data.is_test_student ?? maybeOld.map(old => old.is_test_student).orDefault(false),
        tenantId: Maybe.fromNullable(data.tenant_id).alt(maybeOld.chain(old => old.tenantId)),
        group: Maybe.fromNullable(data.group).map((group) => new apiModels.Group({
            id: group.id,
            createdAt: utils.datetime.toMoment(group.created_at),
            groupSetId: group.group_set_id,
            name: group.name,
            members: group.members.map((member) => new apiModels.UserWithoutGroup({
                id: member.id,
                name: Maybe.fromNullable(member.name),
                isTestStudent: member.is_test_student ?? false,
                tenantId: Maybe.fromNullable(member.tenant_id),
                username: member.username,
            })),
        })),
    };
    return newData.group.caseOf({
        Just: group => new GroupUser(newData, group),
        Nothing: () => new _FrozenNormalUser(newData),
    });
}
